import { IDisplaySlide } from '@pcp/core/interfaces';
import { SlideCapability, SlideHostContext } from '../slide-capability';

/**
 * ScrollVideoScrub — the wheel-to-frame-scrub gesture of a scroll-video slide
 * (`canvas-scroll-video-slides`), INTRINSIC to `SlideType.scrollVideo`.
 *
 * A scroll-video fakes a video with numbered frame images; wheeling steps through them. This
 * capability ONLY claims the wheel (claim-chain, Decision C12) and emits the scrub direction —
 * it never resolves the next frame index, swaps the background, or syncs state (the classroom
 * container owns index math, the ephemeral live stream, and the settled Y.Doc write; the host
 * projects the resulting frame URL back down as the local background). `deltaY > 0` maps to
 * `'left'` (previous frame), matching the legacy SVG renderer's direction convention.
 *
 * The claim happens BEFORE the host's wheel-to-pan fallback — a structural guarantee: a
 * scroll-video is never ALSO a panorama in real content (confirmed 2026-07-14; historical combos
 * were accidental), but if such data ever appeared, wheel would scrub and drag would pan.
 * Contributes no canvas objects and claims no pointers, so drawing and hotspots compose unaffected.
 */
export class ScrollVideoScrub implements SlideCapability {
    private hasScrollVideo = false;

    constructor(
        private readonly ctx: SlideHostContext,
        private readonly onScrub: (direction: 'left' | 'right') => void,
    ) {}

    onSlideChange(slide: IDisplaySlide): void {
        // Belt-and-braces: the recipe only composes this capability for `scrollVideo` slides, but
        // a slide of that type without a payload has nothing to scrub — leave the wheel unclaimed.
        this.hasScrollVideo = !!slide.scrollVideo;
    }

    onWheel(deltaY: number): boolean {
        if (!this.hasScrollVideo || !this.ctx.interaction.isWriter()) return false;
        this.onScrub(deltaY > 0 ? 'left' : 'right');
        return true;
    }

    dispose(): void {
        this.hasScrollVideo = false;
    }
}
